'use client';

import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { Url } from 'next/dist/shared/lib/router/router';

// Returns true to allow navigation, false to block it.
// Receives the triggering click or the destination URL for programmatic navigation.
export type NavigationBlocker = (
	event: React.MouseEvent | Url
) => Promise<boolean>;

export type NavigationBlockingContextProps = {
	addBlocker: ( id: string, blocker: NavigationBlocker ) => void;
	removeBlocker: ( id: string ) => void;
	executeBlockers: (
		event: React.MouseEvent | Url,
		bypassBlockers?: string[]
	) => Promise<boolean>;
	hasBlockers: () => boolean;
};

type NavigationTrapState = History['state'] & {
	__navigationBlockingTrap?: true;
	__navigationBlockingTrapId?: string;
};

export const NavigationBlockingContext =
	createContext<NavigationBlockingContextProps | null>( null );

const getCurrentUrl = () =>
	`${ window.location.pathname }${ window.location.search }${ window.location.hash }`;

const isTrapState = ( state: NavigationTrapState | null | undefined ) =>
	state?.__navigationBlockingTrap === true;

export const NavigationBlockingContextProvider = ( {
	children,
}: {
	children: React.ReactNode;
} ) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const blockersRef = useRef<Map<string, NavigationBlocker>>( new Map() );
	const currentUrlRef = useRef( '' );
	const trapIdRef = useRef<string | null>( null );
	const trapArmedRef = useRef( false );
	const trapInstallingRef = useRef( false );
	const allowNextPopRef = useRef( false );
	const handlingPopRef = useRef( false );

	const addBlocker = useCallback( ( id: string, blocker: NavigationBlocker ) => {
		blockersRef.current.set( id, blocker );
	}, [] );

	const removeBlocker = useCallback( ( id: string ) => {
		blockersRef.current.delete( id );
	}, [] );

	const hasBlockers = useCallback( () => blockersRef.current.size > 0, [] );

	const executeBlockers = useCallback(
		async ( event: React.MouseEvent | Url, bypassBlockers?: string[] ) => {
			for ( const [ id, blocker ] of blockersRef.current.entries() ) {
				if ( bypassBlockers?.includes( id ) ) {
					continue;
				}
				const allowed = await blocker( event );
				if ( !allowed ) {
					return false;
				}
			}
			return true;
		},
		[]
	);

	const armTrap = useCallback( () => {
		if ( trapArmedRef.current || trapInstallingRef.current || !hasBlockers() ) {
			return;
		}

		trapInstallingRef.current = true;

		const trapId = `${ Date.now() }-${ Math.random().toString( 36 ).slice( 2 ) }`;
		const currentState = window.history.state as NavigationTrapState | null;

		window.history.pushState(
			{
				...( currentState || {} ),
				__navigationBlockingTrap: true,
				__navigationBlockingTrapId: trapId,
			},
			'',
			getCurrentUrl()
		);

		trapIdRef.current = trapId;
		trapArmedRef.current = true;
		trapInstallingRef.current = false;
	}, [ hasBlockers ] );

	const disarmTrap = useCallback( () => {
		trapArmedRef.current = false;
		trapIdRef.current = null;
	}, [] );

	useEffect( () => {
		const queryString = searchParams?.toString();
		currentUrlRef.current = queryString
			? `${ pathname }?${ queryString }`
			: pathname;

		const state = window.history.state as NavigationTrapState | null;
		if ( isTrapState( state ) ) {
			disarmTrap();
		}

		if ( hasBlockers() ) {
			armTrap();
		}
	}, [
		armTrap,
		disarmTrap,
		hasBlockers,
		pathname,
		searchParams
	] );

	useEffect( () => {
		const handlePopState = async ( event: PopStateEvent ) => {
			const state = event.state as NavigationTrapState | null;

			if ( allowNextPopRef.current ) {
				allowNextPopRef.current = false;
				disarmTrap();
				return;
			}

			if ( handlingPopRef.current ) {
				return;
			}

			if ( !isTrapState( state ) ) {
				disarmTrap();
				if ( hasBlockers() ) {
					armTrap();
				}
				return;
			}

			if ( state.__navigationBlockingTrapId !== trapIdRef.current ) {
				trapIdRef.current = state.__navigationBlockingTrapId || null;
				trapArmedRef.current = true;
			}

			handlingPopRef.current = true;

			try {
				const allowed = await executeBlockers( currentUrlRef.current );

				if ( allowed ) {
					allowNextPopRef.current = true;
					disarmTrap();
					window.history.back();
					return;
				}

				disarmTrap();
				armTrap();
			} finally {
				handlingPopRef.current = false;
			}
		};

		window.addEventListener( 'popstate', handlePopState );

		return () => {
			window.removeEventListener( 'popstate', handlePopState );
		};
	}, [
		armTrap,
		disarmTrap,
		executeBlockers,
		hasBlockers
	] );

	const value = useMemo(
		() => ( { addBlocker, removeBlocker, executeBlockers, hasBlockers } ),
		[
			addBlocker,
			removeBlocker,
			executeBlockers,
			hasBlockers
		]
	);

	return (
		<NavigationBlockingContext.Provider value={ value }>
			{ children }
		</NavigationBlockingContext.Provider>
	);
};
